/**
 * Rolls the 10 s level windows measured across one recording into the verdict
 * "did this recording carry any audio"
 */
import { combineLevelSamples, type LevelSample } from "./audio-level";
import type { LevelWindow } from "./audio-level-windows";

/**
 * Share of a recording's windows that must be silent before the recording is
 * called silent (a guess)
 */
export const SILENT_RECORDING_FRACTION = 0.9;

export interface RecordingLevelSummary {
  /** Windows that voted: fully sampled, and opened inside the recording. */
  windows: number;
  /** Windows left out of the vote because the sample timer was throttled. */
  throttledWindows: number;
  /**
   * Windows left out because the audio graph was not running, so the analyser's
   * zeros say nothing about what the microphone delivered.
   */
  stoppedGraphWindows: number;
  silentWindows: number;
  /** Zero when no window voted, so a reader never meets a missing number. */
  silentWindowFraction: number;
  silent: boolean;
  /** Level across the whole recording. null when no window voted. */
  level: LevelSample | null;
  /** Every window inside the recording, throttled ones included, in order. */
  history: readonly LevelWindow[];
}

export interface RecordingLevelAccumulator {
  add(window: LevelWindow): void;
  summarise(): RecordingLevelSummary;
}

/**
 * Collects the windows of one recording.
 */
export function createRecordingLevelAccumulator({
  startedAtMs,
}: {
  /** `Date.now()` when the recorder started. */
  startedAtMs: number;
}): RecordingLevelAccumulator {
  const history: LevelWindow[] = [];

  return {
    add(window) {
      if (window.startedAtMs < startedAtMs) return;
      history.push(window);
    },

    summarise() {
      const sampled = history.filter((window) => window.fullySampled);
      const voting = sampled.filter(
        (window) => window.contextState === "running"
      );
      const silent = voting.filter((window) => window.level.silent);
      const fraction = voting.length === 0 ? 0 : silent.length / voting.length;

      return {
        windows: voting.length,
        throttledWindows: history.length - sampled.length,
        stoppedGraphWindows: sampled.length - voting.length,
        silentWindows: silent.length,
        silentWindowFraction: fraction,
        silent: voting.length > 0 && fraction >= SILENT_RECORDING_FRACTION,
        level:
          voting.length === 0
            ? null
            : combineLevelSamples(voting.map((window) => window.level)),
        history,
      };
    },
  };
}
